import type { ReactNode } from 'react';
import clsx from 'clsx';
import './badge.css';

const SharedBadge = ({
  text,
  icon = null,
  variant,
  size = 'sm',
  className = '',
  customColor = '',
  label = '',
  onClick,
  'aria-label': ariaLabel,
}: {
  text?: ReactNode;
  icon?: ReactNode;
  variant?: string;
  size?: string;
  className?: string;
  customColor?: string;
  label?: string;
  onClick?: () => void;
  'aria-label'?: string;
}) => {
  const isHexColor = customColor.startsWith('#');
  const variantStyles = {
    primary: 'badge-primary',
    primaryLight: 'badge-primary-light',
    secondary: 'badge-secondary',
    tertiary: 'badge-tertiary',
    quaternary: 'badge-quaternary',
    warning: 'badge-warning',
    error: 'badge-error',
    custom: isHexColor ? '' : customColor,
  }[variant ?? 'secondary'];

  const sizeStyles = {
    xs: 'text-xs px-1 py-1',
    sm: 'text-sm px-4 py-1',
    md: 'text-base px-6 py-2',
    lg: 'text-lg px-5 py-3',
  }[size];

  const style =
    variant === 'custom' && isHexColor ? { backgroundColor: customColor, color: '#fff' } : undefined;

  const mergedClassName = clsx('badge flex items-center px-3 py-1/.5', variantStyles, sizeStyles, className);
  const inner = (
    <>
      {icon && icon}
      {label && <strong>{label}</strong>}
      {text}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={clsx(mergedClassName, 'cursor-pointer border-0 font-inherit text-left')}
        style={style}
        aria-label={ariaLabel}
      >
        {inner}
      </button>
    );
  }

  return (
    <span className={mergedClassName} style={style}>
      {inner}
    </span>
  );
};

export default SharedBadge;
